import { useState, useEffect, useMemo } from "react";
import { MainLayout } from "./MainLayout";
import { useApp } from "./AppContext";
import { useTranslation } from "../i18n/TranslationContext";
import {
	ChevronLeft,
	ChevronRight,
	MapPin,
	Clock,
	Loader2,
	AlertCircle,
	AlertTriangleIcon,
} from "lucide-react";
import { useApplyToEvents, useCurrentUser } from "../api";
import { useEventsWithStatus } from "../api/events/useEventsWithStatus";
import { useWithdrawFromEvents } from "../api/events/useWithdrawFromEvents";
import { useGetExtra } from "../api/extras/useGetExtra";
import { formatTimeRange, parseEventDate, toLocalDateString } from "../utils/dateUtils";

interface EventSlot {
	id: string;
	date: string;
	displayDate: string;
	dayOfWeek: string;
	time: string;
	location: string;
	booked: number;
	total: number;
	applicants: number;
	selected: boolean;
	event_id: number;
	applied?: boolean;
	application_status?: "applied" | "booked" | "cancelled" | "withdrawn" | "rejected";
	can_withdraw?: boolean;
}

// Helper function to translate application status to German
const translateApplicationStatus = (status: string | undefined): string => {
	switch (status) {
		case "applied":
			return "Beworben";
		case "booked":
			return "Gebucht";
		case "cancelled":
			return "Abgesagt";
		case "withdrawn":
			return "Zurückgezogen";
		case "rejected":
			return "Abgelehnt";
		default:
			return status || "";
	}
};

export function EventApplication() {
	const { t } = useTranslation();
	const {
		navigateDirectly,
		updateUserCompletionState,
		onboardingState,
		completeOnboarding,
		updateOnboardingState,
		continueToNextStep,
	} = useApp();
	const [selectedEventIds, setSelectedEventIds] = useState<number[]>([]);
	const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
	const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [showMarketingDatesModal, setShowMarketingDatesModal] = useState(false);
	const [marketingEventIds, setMarketingEventIds] = useState<number[]>([]);

	// Get current user to get extra_id
	const { user } = useCurrentUser();

	// Check localStorage to see if we've shown this modal before for this user
	const [hasShownMarketingDatesModal, setHasShownMarketingDatesModal] =
		useState(() => {
			if (!user?.extraId) {
				return false;
			}
			// Create a unique key for this user
			const storageKey = `marketingDatesModalShown_${user.extraId}`;
			const hasShown = localStorage.getItem(storageKey) === "true";
			return hasShown;
		});

	// Update the state when user loads
	// Also set hasCompletedOnboarding to prevent redirect to onboarding welcome
	useEffect(() => {
		if (user?.extraId) {
			const storageKey = `marketingDatesModalShown_${user.extraId}`;
			const hasShown = localStorage.getItem(storageKey) === "true";
			setHasShownMarketingDatesModal(hasShown);
		}
		// Set hasCompletedOnboarding to true when user reaches this screen
		updateOnboardingState({
			hasCompletedOnboarding: true,
			isFirstTimeUser: false,
		});
	}, [user?.extraId]);

	// Fetch user profile to check status and marketing dates
	const { extra: userProfile } = useGetExtra(user?.extraId);

	// Check if user can apply to events
	// For external users: Allow applying regardless of occupation proof
	// For internal users: Require ready_to_work status
	const canApplyToEvents = userProfile?.profile_status === "ready_to_work";
	// Fetch events with application status from API (based on user type)
	const eventType = user?.internal_extra ? "internal" : "external";
	const { events, loading, refetch, error } = useEventsWithStatus(
		user?.extraId || null,
		eventType
	);

	// Apply to events hook
	const { applyToEvents, loading: applyLoading } = useApplyToEvents();

	// Withdraw from events hook
	const { withdrawFromEvents, loading: withdrawLoading } =
		useWithdrawFromEvents();

	// Pre-select events that are already applied when events load
	useEffect(() => {
		if (events && events.length > 0) {
			const appliedEventIds = events
				.filter(
					(event) => event.applied && event.application_status === "applied"
				)
				.map((event) => event.event_id);

			if (appliedEventIds.length > 0) {
				setSelectedEventIds(appliedEventIds);
			}
		}
	}, [events]);

	// Check for marketing dates on first load
	useEffect(() => {
		if (
			!hasShownMarketingDatesModal &&
			events &&
			events.length > 0 &&
			userProfile?.marketing_dates &&
			userProfile.marketing_dates.length > 0
		) {
			// Convert marketing dates to comparable format (YYYY-MM-DD)
			const marketingDatesSet = new Set(
				userProfile.marketing_dates.map((date) => {
					// Marketing dates are now in DD.MM.YYYY format
					const parts = date.split(".");
					if (parts.length === 3) {
						const day = parts[0].padStart(2, "0");
						const month = parts[1].padStart(2, "0");
						const year = parts[2];
						const formattedDate = `${year}-${month}-${day}`;
						return formattedDate;
					}
					return date; // fallback in case of unexpected format
				})
			);

			// Find events that match marketing dates
			const matchingEvents = events.filter((event) => {
				const eventDate = toLocalDateString(event.event_date);
				const matches = marketingDatesSet.has(eventDate);
				if (matches) {
				}
				return matches;
			});

			// If we have matching events and they aren't already applied, show the modal
			if (matchingEvents.length > 0) {
				const unappliedMarketingEvents = matchingEvents.filter(
					(event) => !event.applied || event.application_status !== "applied"
				);

				if (unappliedMarketingEvents.length > 0) {
					setMarketingEventIds(unappliedMarketingEvents.map((e) => e.event_id));
					setShowMarketingDatesModal(true);
					setHasShownMarketingDatesModal(true);

					// Save to localStorage that we've shown this modal for this user
					const storageKey = `marketingDatesModalShown_${user?.extraId || "unknown"}`;
					localStorage.setItem(storageKey, "true");
				} else {
				}
			} else {
			}
		}
	}, [events, userProfile, hasShownMarketingDatesModal, onboardingState]);

	// Static data for Frankfurt Airport Inspector
	const jobTitle = "Frankfurt Airport Inspector";
	const jobLocation = "Frankfurt Airport, Terminal 3";

	// const shortDescriptionText = t("eventApplication.shortDescription");
	// const fullDescriptionText = t("eventApplication.fullDescription");

	// Helper function to get date (German style - number with period)
	const getOrdinalDate = (day: number): string => {
		return `${day}`;
	};

	// Helper function to get day of week name
	const getDayOfWeekName = (date: Date): string => {
		const days = [
			t("dateTime.sunday"),
			t("dateTime.monday"),
			t("dateTime.tuesday"),
			t("dateTime.wednesday"),
			t("dateTime.thursday"),
			t("dateTime.friday"),
			t("dateTime.saturday"),
		];
		return days[date.getDay()];
	};

	// Helper function to get month/year string
	const getMonthYearString = (date: Date): string => {
		const months = [
			t("dateTime.january"),
			t("dateTime.february"),
			t("dateTime.march"),
			t("dateTime.april"),
			t("dateTime.may"),
			t("dateTime.june"),
			t("dateTime.july"),
			t("dateTime.august"),
			t("dateTime.september"),
			t("dateTime.october"),
			t("dateTime.november"),
			t("dateTime.december"),
		];
		return `${months[date.getMonth()]} ${date.getFullYear()}`;
	};

	// Process events into calendar slots grouped by month
	const processedEventsByMonth = useMemo(() => {
		if (!events || events.length === 0) return [];

		// Group events by month
		const eventsByMonth = new Map<string, EventSlot[]>();

		events.forEach((event) => {
			const eventDate = parseEventDate(event.event_date) || new Date(event.event_date);
			const monthKey = getMonthYearString(eventDate);
			const dayOfWeek = getDayOfWeekName(eventDate);

			// Only show Tuesday and Thursday events (or adjust based on actual data)
			const slot: EventSlot = {
				id: `event-${event.event_id}`,
				date: event.event_date,
				displayDate: getOrdinalDate(eventDate.getDate()),
				dayOfWeek: dayOfWeek,
				time: formatTimeRange(event.start_time, event.end_time),
				location: t("eventApplication.terminalLocation"),
				booked: event.extra_booked,
				total: event.extra_required,
				selected: false,
				event_id: event.event_id,
				applied: event.applied,
				application_status: event.application_status,
				can_withdraw: event.can_withdraw,
				applicants: event.extra_applied,
			};

			if (!eventsByMonth.has(monthKey)) {
				eventsByMonth.set(monthKey, []);
			}
			eventsByMonth.get(monthKey)!.push(slot);
		});

		// Convert to array and sort by date
		const monthsArray = Array.from(eventsByMonth.entries()).map(
			([month, events]) => ({
				month,
				events: events.sort(
					(a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
				),
			})
		);

		// Sort months chronologically
		monthsArray.sort((a, b) => {
			const dateA = new Date(a.events[0].date);
			const dateB = new Date(b.events[0].date);
			return dateA.getTime() - dateB.getTime();
		});

		return monthsArray;
	}, [events]);

	// Get current month data
	const currentMonthData = processedEventsByMonth[currentMonthIndex] || {
		month: "",
		events: [],
	};

	// Pre-select applied events when events load
	useEffect(() => {
		if (events && events.length > 0) {
			const appliedEventIds = events
				.filter(
					(event) => event.applied && event.application_status === "applied"
				)
				.map((event) => event.event_id);
			setSelectedEventIds(appliedEventIds);
		}
	}, [events]);

	// Organize events into proper weekly grid structure
	const organizeEventsIntoWeeklyColumns = (monthEvents: EventSlot[]) => {
		// Sort events by actual date first
		const sortedEvents = [...monthEvents].sort(
			(a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
		);

		// Get unique day names from the events
		const uniqueDays = [...new Set(sortedEvents.map(event => event.dayOfWeek))];
		
		// Define preferred day order (Tuesday first, then Thursday)
		const dayOrderPreference = ["Dienstag", "Donnerstag", "Montag", "Mittwoch", "Freitag", "Samstag", "Sonntag"];
		
		// Sort active days according to preference
		const activeDays = uniqueDays.sort((a, b) => {
			const indexA = dayOrderPreference.indexOf(a);
			const indexB = dayOrderPreference.indexOf(b);
			// If day not found in preference, put it at the end
			return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
		});

		// Helper function to get week start (Monday) for a given date
		const getWeekStart = (date: Date): string => {
			const d = new Date(date);
			const day = d.getDay();
			const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
			const monday = new Date(d.setDate(diff));
			const year = monday.getFullYear();
			const month = String(monday.getMonth() + 1).padStart(2, '0');
			const dayStr = String(monday.getDate()).padStart(2, '0');
			return `${year}-${month}-${dayStr}`;
		};

		// Group events by week
		const eventsByWeek: { [weekStart: string]: { [day: string]: EventSlot | null } } = {};

		sortedEvents.forEach(event => {
			const eventDate = parseEventDate(event.date) || new Date(event.date);
			const weekStart = getWeekStart(eventDate);
			
			if (!eventsByWeek[weekStart]) {
				// Initialize week with all days as null
				eventsByWeek[weekStart] = {};
				activeDays.forEach(day => {
					eventsByWeek[weekStart][day] = null;
				});
			}
			
			// Place event in its day column for this week
			eventsByWeek[weekStart][event.dayOfWeek] = event;
		});

		// Convert to sorted array of rows (weeks)
		const rows = Object.keys(eventsByWeek)
			.sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
			.map(weekStart => eventsByWeek[weekStart]);

		// Create day columns for backward compatibility
		const dayColumns: { [key: string]: EventSlot[] } = {};
		activeDays.forEach(day => {
			dayColumns[day] = sortedEvents.filter(event => event.dayOfWeek === day);
		});

		return { dayColumns, rows, activeDays };
	};

	const {
		dayColumns,
		rows: calendarRows,
		activeDays,
	} = organizeEventsIntoWeeklyColumns(currentMonthData.events);

	const toggleEventSelection = (eventId: number) => {
		setSelectedEventIds((prev) => {
			if (prev.includes(eventId)) {
				return prev.filter((id) => id !== eventId);
			} else {
				return [...prev, eventId];
			}
		});
	};

	// Calculate the number of events that will actually change
	const getChangedEventsCount = () => {
		let changeCount = 0;

		events.forEach((event) => {
			const isSelected = selectedEventIds.includes(event.event_id);
			const isApplied = event.applied && event.application_status === "applied";
			const isWithdrawn =
				event.applied && event.application_status === "withdrawn";
			const isCancelled =
				event.applied && event.application_status === "cancelled";
			const isRejected =
				event.applied && event.application_status === "rejected";

			// Count events that will be applied (selected and currently not applied, withdrawn, cancelled, or rejected)
			if (isSelected && (!event.applied || isWithdrawn || isCancelled || isRejected)) {
				changeCount++;
			}
			// Count ALL events that will be withdrawn (including those that cannot be withdrawn)
			// This ensures the button shows changes even for group-applied events
			else if (!isSelected && isApplied) {
				changeCount++;
			}
		});

		return changeCount;
	};

	const handleShowConfirmation = () => {
		// Only show confirmation if there are actual changes to save
		const changeCount = getChangedEventsCount();
		if (changeCount === 0) return;
		setShowConfirmationModal(true);
	};

	const handleConfirmApplication = async () => {
		if (!user?.extraId) return;

		// Separate events into those to apply and those to withdraw
		const eventsToApply: number[] = [];
		const eventsToWithdraw: number[] = [];

		// Check all events (not just selected ones) to determine what needs to be withdrawn
		events.forEach((event) => {
			const isSelected = selectedEventIds.includes(event.event_id);
			const isApplied = event.applied && event.application_status === "applied";
			const isWithdrawn =
				event.applied && event.application_status === "withdrawn";
			const isCancelled =
				event.applied && event.application_status === "cancelled";
			const isRejected =
				event.applied && event.application_status === "rejected";

			if (isSelected && (!event.applied || isWithdrawn || isCancelled || isRejected)) {
				// Event is selected and either new, withdrawn, cancelled, or rejected → apply
				eventsToApply.push(event.event_id);
			} else if (!isSelected && isApplied) {
				// Event is not selected but is currently applied → withdraw
				eventsToWithdraw.push(event.event_id);
			}
			// Note: If selected and already applied, no change needed
			// Note: If not selected and not applied, no change needed
		});

		// If nothing to change, just close modal
		if (eventsToApply.length === 0 && eventsToWithdraw.length === 0) {
			setShowConfirmationModal(false);
			return;
		}

		try {
			// Apply to new events
			if (eventsToApply.length > 0) {
				const applyResult = await applyToEvents({
					extra_id: user.extraId,
					event_ids: eventsToApply,
				});
				if (applyResult) {
					updateUserCompletionState({
						event_applied: true,
						event_confirmed: true,
					});

					// Check if user is in onboarding flow and this was the event application step
					const isInOnboarding =
						!onboardingState.hasCompletedOnboarding &&
						onboardingState.isFirstTimeUser;
					const isEventApplicationStep =
						onboardingState.currentOnboardingStep === 2; // Event application is step 2

					if (isInOnboarding && isEventApplicationStep) {
						// Mark event application step as completed in onboarding
						continueToNextStep(2, { applied_events: eventsToApply });

						// Navigate to next onboarding step or complete onboarding if this was the last step
						// The continueToNextStep function will handle the navigation
						return; // Return early to avoid the usual modal close behavior
					}
				}
			}

			// Withdraw from deselected events
			if (eventsToWithdraw.length > 0) {
				const withdrawResult = await withdrawFromEvents({
					extra_id: user.extraId,
					event_ids: eventsToWithdraw,
				});
				if (withdrawResult) {
				}
			}

			// Refresh events to show updated statuses
			await refetch();

			// Close modal and stay on the same screen to see the updated status
			setShowConfirmationModal(false);
		} catch (error) {
			console.error("Failed to update event applications:", error);
			setShowConfirmationModal(false);
		}
	};

	const handleMonthNavigation = (direction: "prev" | "next") => {
		if (direction === "prev" && currentMonthIndex > 0) {
			setCurrentMonthIndex(currentMonthIndex - 1);
		} else if (
			direction === "next" &&
			currentMonthIndex < processedEventsByMonth.length - 1
		) {
			setCurrentMonthIndex(currentMonthIndex + 1);
		}
	};

	const toggleDescription = () => {
		setIsDescriptionExpanded(!isDescriptionExpanded);
	};

	// Show loading state
	if (loading) {
		return (
			<MainLayout>
				<div className="flex flex-col items-center justify-center min-h-[60vh]">
					<Loader2 className="w-8 h-8 animate-spin text-[var(--ucm-primary-teal)]" />
					<p className="mt-4 text-[var(--ucm-text-medium)]">
						Loading events...
					</p>
				</div>
			</MainLayout>
		);
	}

	// Show error state
	if (error) {
		return (
			<MainLayout showBack={true} onBack={() => navigateDirectly("dashboard")}>
				<div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
					<p className="text-[var(--ucm-error-red)] mb-4">
						Failed to load events
					</p>
					<button
						onClick={() => refetch()}
						className="px-6 py-2 bg-[var(--ucm-primary-teal)] text-white rounded-lg"
					>
						{t("eventApplication.retry")}
					</button>
				</div>
			</MainLayout>
		);
	}

	// Show no events state
	if (processedEventsByMonth.length === 0) {
		return (
			<MainLayout showBack={true} onBack={() => navigateDirectly("dashboard")}>
				<div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
					<p className="text-[var(--ucm-text-medium)] mb-4">
						{t("eventApplication.noEventsAvailable")}
					</p>
					<p className="text-sm text-[var(--ucm-text-light)]">
						{t("eventApplication.checkBackLater")}
					</p>
				</div>
			</MainLayout>
		);
	}

	// Get selected events details for confirmation modal
	const selectedEvents = events.filter((e) =>
		selectedEventIds.includes(e.event_id)
	);

	// Get events that will be applied and withdrawn
	const getEventsToChange = () => {
		const toApply: typeof events = [];
		const toWithdraw: typeof events = [];
		const cannotWithdraw: typeof events = [];

		events.forEach((event) => {
			const isSelected = selectedEventIds.includes(event.event_id);
			const isApplied = event.applied && event.application_status === "applied";
			const isWithdrawn =
				event.applied && event.application_status === "withdrawn";
			const isCancelled =
				event.applied && event.application_status === "cancelled";
			const isRejected =
				event.applied && event.application_status === "rejected";

			if (isSelected && (!event.applied || isWithdrawn || isCancelled || isRejected)) {
				toApply.push(event);
			} else if (!isSelected && isApplied) {
				// Separate events that cannot be withdrawn
				if (event.can_withdraw === false) {
					cannotWithdraw.push(event);
				} else {
					toWithdraw.push(event);
				}
			}
		});

		return { toApply, toWithdraw, cannotWithdraw };
	};

	const {
		toApply: eventsToApply,
		toWithdraw: eventsToWithdraw,
		cannotWithdraw: eventsCannotWithdraw,
	} = getEventsToChange();
	// Include events that cannot be withdrawn in the count so the button is enabled
	const changedEventsCount =
		eventsToApply.length +
		eventsToWithdraw.length +
		eventsCannotWithdraw.length;

	// Handler for marketing dates confirmation
	const handleMarketingDatesConfirm = async () => {
		if (!user?.extraId || marketingEventIds.length === 0) return;

		try {
			// Apply to the marketing date events
			const result = await applyToEvents({
				extra_id: user.extraId,
				event_ids: marketingEventIds,
			});

			if (result) {
				// Update the selected events to include these
				setSelectedEventIds((prev) => {
					const newIds = [...prev];
					marketingEventIds.forEach((id) => {
						if (!newIds.includes(id)) {
							newIds.push(id);
						}
					});
					return newIds;
				});

				// Update user completion state if needed
				updateUserCompletionState({
					event_applied: true,
					event_confirmed: true,
				});

				// Refresh events to show updated statuses
				await refetch();

				// Close the modal
				setShowMarketingDatesModal(false);

				// If in onboarding flow, handle navigation
				const isInOnboarding =
					onboardingState.isFirstTimeUser &&
					!onboardingState.hasCompletedOnboarding;
				const isEventApplicationStep =
					onboardingState.currentOnboardingStep === 2;

				if (isInOnboarding && isEventApplicationStep) {
					// Continue to next onboarding step
					continueToNextStep(2, { applied_events: marketingEventIds });
				}
			}
		} catch (error) {
			console.error("Failed to apply to marketing date events:", error);
		}
	};

	const handleMarketingDatesCancel = () => {
		setShowMarketingDatesModal(false);
	};

	// Get marketing events details
	const marketingEvents = events.filter((e) =>
		marketingEventIds.includes(e.event_id)
	);

	return (
		<>
			{/* Marketing Dates Confirmation Modal */}
			{showMarketingDatesModal && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto m-auto">
						<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
							{t("eventApplication.marketingDatesTitle")}
						</h2>

						<div className="mb-6">
							<p className="text-sm text-[var(--ucm-text-medium)] mb-4">
								{t("eventApplication.marketingDatesDescription")}
							</p>

							{/* Show the pre-selected marketing events */}
							<div className="space-y-2">
								<p className="text-sm font-semibold text-[var(--ucm-text-dark)] mb-2">
									{t("eventApplication.marketingDatesPreselected")}
								</p>
								{marketingEvents.map((event) => (
									<div
										key={event.event_id}
										className="p-3 bg-[var(--ucm-light-green)] rounded-lg"
									>
										<div className="flex items-center justify-between">
											<div>
												<p className="text-sm font-medium text-[var(--ucm-dark-green)]">
													{(() => {
														const date = parseEventDate(event.event_date) || new Date(event.event_date);
														const day = date
															.getDate()
															.toString()
															.padStart(2, "0");
														const month = (date.getMonth() + 1)
															.toString()
															.padStart(2, "0");
														const year = date.getFullYear();
														return `${day}.${month}.${year}`;
													})()}
												</p>
												<p className="text-xs text-[var(--ucm-dark-green)]">
													{formatTimeRange(event.start_time, event.end_time)} •{" "}
													{t("eventApplication.terminalLocation")}
												</p>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>

						<div className="flex space-x-3">
							<button
								onClick={handleMarketingDatesCancel}
								disabled={applyLoading}
								className="flex-1 h-12 bg-white border-2 border-[var(--ucm-border-light)] text-[var(--ucm-text-dark)] font-semibold rounded-full hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{t("common.cancel")}
							</button>
							<button
								onClick={handleMarketingDatesConfirm}
								disabled={applyLoading || marketingEventIds.length === 0}
								className={`flex-1 h-12 bg-[var(--ucm-success-green)] text-white font-semibold rounded-full transition-colors duration-200 flex items-center justify-center gap-2 ${
									applyLoading
										? "opacity-70 cursor-not-allowed"
										: "hover:bg-[var(--ucm-dark-green)]"
								}`}
							>
								{applyLoading ? (
									<>
										<Loader2 className="w-4 h-4 animate-spin" />
										{t("eventApplication.applying")}
									</>
								) : (
									t("eventApplication.applyToSelected")
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Confirmation Modal */}
			{showConfirmationModal && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto m-auto">
						<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
							{t("eventApplication.confirmChanges")}
						</h2>

						<div className="mb-6">
							<p className="text-sm text-[var(--ucm-text-medium)] mb-4">
								{t("eventApplication.changesDescription")}
							</p>

							{/* Events to Apply */}
							{eventsToApply.length > 0 && (
								<div className="mb-4">
									<h3 className="text-sm font-semibold text-green-700 mb-2">
										✓ {t("eventApplication.applyTo")} {eventsToApply.length}{" "}
										{eventsToApply.length !== 1
											? t("eventApplication.events")
											: t("eventApplication.event")}
										:
									</h3>
									<div className="space-y-2 max-h-[150px] overflow-y-auto">
										{eventsToApply.map((event) => {
											const eventDate = parseEventDate(event.event_date) || new Date(event.event_date);
											return (
												<div
													key={event.event_id}
													className="bg-green-50 border border-green-200 rounded-lg p-3"
												>
													<div className="font-semibold text-sm text-[var(--ucm-text-dark)]">
														{getDayOfWeekName(eventDate)},{" "}
														{getOrdinalDate(eventDate.getDate())}{" "}
														{getMonthYearString(eventDate).split(" ")[0]}
													</div>
													<div className="flex items-center justify-between text-xs text-[var(--ucm-text-medium)] mt-1">
														<span>
															{formatTimeRange(
																event.start_time,
																event.end_time
															)}
														</span>
														<div className="text-right">
															<span className="opacity-80">
																{Math.max(
																	0,
																	event.extra_required - event.extra_booked
																)}{" "}
																von {event.extra_required} verfügbar
															</span>
														</div>
													</div>
												</div>
											);
										})}
									</div>
								</div>
							)}

							{/* Events to Withdraw */}
							{eventsToWithdraw.length > 0 && (
								<div>
									<h3 className="text-sm font-semibold text-red-600 mb-2">
										✗ {t("eventApplication.withdrawFrom")}{" "}
										{eventsToWithdraw.length}{" "}
										{eventsToWithdraw.length !== 1
											? t("eventApplication.events")
											: t("eventApplication.event")}
										:
									</h3>
									<div className="space-y-2 max-h-[150px] overflow-y-auto">
										{eventsToWithdraw.map((event) => {
											const eventDate = parseEventDate(event.event_date) || new Date(event.event_date);
											return (
												<div
													key={event.event_id}
													className="bg-red-50 border border-red-200 rounded-lg p-3"
												>
													<div className="font-semibold text-sm text-[var(--ucm-text-dark)]">
														{getDayOfWeekName(eventDate)},{" "}
														{getOrdinalDate(eventDate.getDate())}{" "}
														{getMonthYearString(eventDate).split(" ")[0]}
													</div>
													<div className="flex items-center justify-between text-xs text-[var(--ucm-text-medium)] mt-1">
														<span>
															{formatTimeRange(
																event.start_time,
																event.end_time
															)}
														</span>
														<div className="text-right">
															<span className="opacity-80">
																{Math.max(
																	0,
																	event.extra_required - event.extra_booked
																)}{" "}
																von {event.extra_required} verfügbar
															</span>
														</div>
													</div>
												</div>
											);
										})}
									</div>
								</div>
							)}

							{/* Events that Cannot be Withdrawn (Group Booked) */}
							{eventsCannotWithdraw.length > 0 && (
								<div className="mt-4">
									<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
										<h3 className="text-sm font-semibold text-yellow-800 mb-2">
											⚠️ {t("eventApplication.cannotWithdrawTitle")}
										</h3>
										<p className="text-xs text-yellow-700 mb-3">
											{t("eventApplication.cannotWithdrawDescription")}
										</p>
										<div className="space-y-2 max-h-[150px] overflow-y-auto">
											{eventsCannotWithdraw.map((event) => {
												const eventDate = parseEventDate(event.event_date) || new Date(event.event_date);
												return (
													<div
														key={event.event_id}
														className="bg-white border border-yellow-200 rounded-lg p-3"
													>
														<div className="font-semibold text-sm text-[var(--ucm-text-dark)]">
															{getDayOfWeekName(eventDate)},{" "}
															{getOrdinalDate(eventDate.getDate())}{" "}
															{getMonthYearString(eventDate).split(" ")[0]}
														</div>
														<div className="flex items-center justify-between text-xs text-[var(--ucm-text-medium)] mt-1">
															<span>
																{formatTimeRange(
																	event.start_time,
																	event.end_time
																)}
															</span>
															<span className="text-yellow-700 font-medium">
																{t("eventApplication.groupApplied")}
															</span>
														</div>
													</div>
												);
											})}
										</div>
									</div>
								</div>
							)}
						</div>

						<div className="flex gap-3">
							<button
								onClick={() => setShowConfirmationModal(false)}
								disabled={applyLoading}
								className="flex-1 bg-white border border-[var(--ucm-border-light)] text-[var(--ucm-text-dark)] rounded-xl p-3 font-medium transition-colors hover:bg-[var(--ucm-light-gray)] disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{t("eventApplication.cancel")}
							</button>
							<button
								onClick={handleConfirmApplication}
								disabled={applyLoading || withdrawLoading}
								className={`flex-1 bg-[var(--ucm-primary-teal)] text-white rounded-xl p-3 font-medium transition-colors flex items-center justify-center gap-2 ${
									applyLoading || withdrawLoading
										? "opacity-70 cursor-not-allowed"
										: "hover:bg-[var(--ucm-primary-teal-hover)]"
								}`}
							>
								{applyLoading || withdrawLoading ? (
									<>
										<Loader2 className="w-4 h-4 animate-spin" />
										{t("eventApplication.updating")}
									</>
								) : (
									t("eventApplication.confirm")
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			<MainLayout showBack={true} onBack={() => navigateDirectly("dashboard")}>
				{/* Job Header Section */}
				{/* <div className="job-header-section bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)] mb-6"> */}
				{/* Job Title */}
				{/* <h1 className="job-title text-[28px] font-bold text-[var(--ucm-text-dark)] mb-5 leading-tight">
						{jobTitle}
					</h1> */}

				{/* Location Info */}
				{/* <div className="info-item flex items-center gap-3 mb-4">
						<div className="info-icon w-10 h-10 bg-[var(--ucm-light-blue)] rounded-full flex items-center justify-center text-lg">
							<MapPin size={20} className="text-[var(--ucm-primary-teal)]" />
						</div>
						<div className="info-text flex-1">
							<span className="location-name text-base font-semibold text-[var(--ucm-text-dark)] underline">
								{jobLocation}
							</span>
						</div>
					</div> */}

				{/* Job Description */}
				{/* <div className="job-description pt-5 border-t border-[var(--ucm-border-light)]">
						<p className="description-text text-sm text-[var(--ucm-text-medium)] leading-relaxed mb-3 text-left">
							{isDescriptionExpanded
								? fullDescriptionText
								: shortDescriptionText}
						</p>
						<button
							onClick={toggleDescription}
							className="see-more-btn bg-none border-none text-[var(--ucm-text-medium)] text-sm font-medium cursor-pointer p-0 underline"
						>
							{isDescriptionExpanded
								? t("eventApplication.seeLess")
								: t("eventApplication.seeMore")}
						</button>
					</div> */}
				{/* </div> */}

				{/* Calendar Section */}
				<div className="calendar-section px-0 mb-6">
					{/* Section Title */}
					<h2 className="section-title text-xl font-semibold text-[var(--ucm-text-dark)] mb-5 px-6">
						{t("eventApplication.availableDates")}
					</h2>

					{/* Month Navigation */}
					<div className="month-navigation flex items-center justify-between mb-6 px-6">
						<button
							onClick={() => handleMonthNavigation("prev")}
							disabled={currentMonthIndex === 0}
							className={`nav-arrow bg-none border-none text-2xl cursor-pointer p-2 rounded-lg transition-colors ${
								currentMonthIndex === 0
									? "text-[var(--ucm-text-light)] cursor-not-allowed"
									: "text-[var(--ucm-text-dark)] hover:bg-[var(--ucm-light-gray)]"
							}`}
						>
							<ChevronLeft size={24} />
						</button>
						<h3 className="month-title text-lg font-semibold text-[var(--ucm-text-dark)]">
							{currentMonthData.month || t("eventApplication.noEvents")}
						</h3>
						<button
							onClick={() => handleMonthNavigation("next")}
							disabled={currentMonthIndex === processedEventsByMonth.length - 1}
							className={`nav-arrow bg-none border-none text-2xl cursor-pointer p-2 rounded-lg transition-colors ${
								currentMonthIndex === processedEventsByMonth.length - 1
									? "text-[var(--ucm-text-light)] cursor-not-allowed"
									: "text-[var(--ucm-text-dark)] hover:bg-[var(--ucm-light-gray)]"
							}`}
						>
							<ChevronRight size={24} />
						</button>
					</div>

					{/* Calendar Grid */}
					{currentMonthData.events.length > 0 && (
						<div className="calendar-grid px-6 overflow-x-auto">
							{/* Day Headers */}
							<div
								className={`day-headers grid gap-2 mb-3 ${
									activeDays.length <= 3 ? "max-w-2xl mx-auto" : ""
								}`}
								style={{
									gridTemplateColumns: `repeat(${activeDays.length}, minmax(140px, 1fr))`,
								}}
							>
								{activeDays.map((day) => (
									<div
										key={day}
										className="day-header text-sm font-semibold text-[var(--ucm-text-medium)] text-center"
									>
										{day}
									</div>
								))}
							</div>

							{/* Date Grid */}
							<div
								className={`date-grid space-y-2 ${
									activeDays.length <= 3 ? "max-w-2xl mx-auto" : ""
								}`}
							>
								{calendarRows.map((row, rowIndex) => (
									<div
										key={rowIndex}
										className="grid gap-2"
										style={{
											gridTemplateColumns: `repeat(${activeDays.length}, minmax(140px, 1fr))`,
										}}
									>
										{activeDays.map((day) => {
											const event = row[day];
											return (
												<div
													key={`${rowIndex}-${day}`}
													className="day-cell flex justify-center"
												>
													{event ? (
														event.applied &&
														event.application_status === "booked" ? (
															// Only disable for booked events
															<div
																className="date-slot relative rounded-xl overflow-hidden min-h-[110px] w-full max-w-[160px] flex flex-col cursor-not-allowed opacity-60 bg-gray-50"
																style={{
																	pointerEvents: "none",
																}}
															>
																{/* Main content area - consistent padding for all cards */}
																<div className="flex-1 flex flex-col justify-center items-center p-3 pb-8">
																	<span className="date-number text-base font-semibold">
																		{event.displayDate}
																	</span>
																	<span className="time text-sm whitespace-nowrap opacity-80 mt-2">
																		{event.time}
																	</span>
																	<span className="block text-xs font-medium my-2">
																		{event.applicants}{" "}
																		{t("eventApplication.applicant")}
																	</span>
																	<span className="availability text-xs text-black mt-1">
																		{Math.max(0, event.total - event.booked)} von{" "}
																		{event.total} verfügbar
																	</span>
																</div>

																{/* Footer with status for booked */}
																{event.applied && event.application_status && (
																	<div className="bg-green-50 text-green-700 border-green-200 absolute bottom-0 left-0 right-0 px-2 py-1 text-center border-t">
																		<span className="text-[10px] font-medium uppercase tracking-wider">
																			✓{" "}
																			{translateApplicationStatus(
																				event.application_status
																			)}
																		</span>
																	</div>
																)}
															</div>
														) : (
															// Button for selectable events
															<button
																onClick={() => {
																	const isWithdrawn =
																		event.applied &&
																		event.application_status === "withdrawn";
																	const isApplied =
																		event.applied &&
																		event.application_status === "applied";
																	const isCancelled =
																		event.applied &&
																		event.application_status === "cancelled";
																	const isRejected =
																		event.applied &&
																		event.application_status === "rejected";
																	const canSelect =
																		canApplyToEvents &&
																		(!event.applied ||
																			isWithdrawn ||
																			isApplied ||
																			isCancelled ||
																			isRejected);
																	if (canSelect) {
																		toggleEventSelection(event.event_id);
																	}
																}}
																disabled={!canApplyToEvents}
																className={`date-slot relative rounded-xl overflow-hidden transition-all duration-200 min-h-[110px] w-full max-w-[160px] flex flex-col ${
																	selectedEventIds.includes(event.event_id) &&
																	(!event.applied ||
																		event.application_status === "withdrawn" ||
																		event.application_status === "applied" ||
																		event.application_status === "cancelled" ||
																		event.application_status === "rejected")
																		? "selected"
																		: ""
																}`}
																style={{
																	opacity: !canApplyToEvents ? 0.5 : 1,
																	cursor: !canApplyToEvents
																		? "not-allowed"
																		: "pointer",
																}}
															>
																{/* Main content area - consistent padding for all cards */}
																<div className="flex-1 flex flex-col justify-center items-center p-3 pb-8">
																	<span className="date-number text-base font-semibold">
																		{event.displayDate}
																	</span>
																	<span className="time text-sm whitespace-nowrap opacity-80 mt-2">
																		{event.time}
																	</span>
																	<span className="block text-xs font-medium my-2">
																		{event.applicants}{" "}
																		{t("eventApplication.applicant")}
																	</span>
																	<span className="availability text-xs text-black mt-1">
																		{Math.max(0, event.total - event.booked)} von{" "}
																		{event.total} verfügbar
																	</span>
																</div>

																{/* Checkmark for selected events (no status, withdrawn, applied, cancelled, or rejected) */}
																{selectedEventIds.includes(event.event_id) &&
																	(!event.applied ||
																		event.application_status === "withdrawn" ||
																		event.application_status === "applied" ||
																		event.application_status === "cancelled" ||
																		event.application_status === "rejected") && (
																		<span className="selected-checkmark absolute top-2 right-2 text-white text-sm font-bold">
																			✓
																		</span>
																	)}

																{/* Footer with status - show for all statuses including withdrawn */}
																{event.applied && event.application_status && (
																	<div
																		className={`absolute bottom-0 left-0 right-0 px-2 py-1 text-center border-t ${
																			event.application_status === "booked"
																				? "bg-green-50 text-green-700 border-green-200"
																				: event.application_status === "applied"
																					? "bg-gray-50 text-gray-600 border-gray-200"
																					: event.application_status ===
																						  "cancelled"
																						? "bg-red-50 text-red-600 border-red-200"
																						: event.application_status ===
																							  "withdrawn"
																							? "bg-gray-50 text-gray-600 border-gray-200"
																							: event.application_status ===
																								  "rejected"
																								? "bg-orange-50 text-orange-600 border-orange-200"
																								: "bg-gray-50 text-gray-600 border-gray-200"
																		}`}
																	>
																		<span className="text-[10px] font-medium uppercase tracking-wider">
																			{event.application_status === "booked"
																				? "✓ " +
																					translateApplicationStatus("booked")
																				: translateApplicationStatus(
																						event.application_status
																					)}
																		</span>
																	</div>
																)}
															</button>
														)
													) : (
														<div className="date-slot-empty min-h-[110px] max-w-[160px] w-full"></div>
													)}
												</div>
											);
										})}
									</div>
								))}
							</div>
						</div>
					)}
				</div>

				{/* Bottom Section */}
				<div className="application-edit-section p-5 border-t border-[var(--ucm-border-light)] bg-[var(--ucm-light-gray)] mt-6">
					{/* Profile Incomplete Warning */}
					{!canApplyToEvents && (
						<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
							<div className="flex items-center gap-1">
								<AlertTriangleIcon className="w-5 h-5 text-yellow-800" />
								<h3 className="text-sm font-semibold text-yellow-800 mb-1">
									Profil unvollständig
								</h3>
							</div>

							<p className="text-sm text-yellow-700">
								Bitte vervollständigen Sie Ihr Profil, um
								Termine auswählen zu können.
							</p>
						</div>
					)}
					<button
						onClick={handleShowConfirmation}
						disabled={changedEventsCount === 0 || !canApplyToEvents}
						className={`edit-application-btn w-full bg-white border border-[var(--ucm-border-light)] rounded-xl p-4 text-base font-medium text-[var(--ucm-text-dark)] cursor-pointer transition-all duration-200 flex items-center justify-center gap-2 ${
							changedEventsCount > 0 && canApplyToEvents
								? "hover:border-[var(--ucm-primary-teal)] hover:bg-[var(--ucm-light-blue)]"
								: "opacity-50 cursor-not-allowed"
						}`}
					>
						{changedEventsCount === 0
							? t("eventApplication.noChangesToSave")
							: `${t("eventApplication.saveChanges")} (${changedEventsCount} ${changedEventsCount !== 1 ? t("eventApplication.events") : t("eventApplication.event")})`}
					</button>
				</div>
			</MainLayout>
		</>
	);
}
